// One-time Manager welcome walkthrough (PROJECT.md Manager-portal gap #2).
//
// Shown exactly once, on a Manager's first successful sign-in — the backend
// (`landingPathFor` in `apps/api/src/routes/auth.ts`) routes here instead of
// `/manager` only when `firstSignInAt` was null at verify time, and stamps it
// in the same request, so this page is never shown again automatically. It
// stays reachable from `/manager/settings`.
//
// A NORMAL `/manager/*` PAGE, inside `ManagerShell` (PO 2026-08-06). It first
// shipped as a chrome-free top-level route so nothing competed with the "Get
// started" call to action; the responsive conversion then left it as the only
// Manager page that doesn't pick its chrome by viewport, so revisiting it from
// Settings on a desktop dropped you into a bare narrow column that reads as
// the phone layout. Hence: no `<main>` here (the shell owns the scroll
// container and the page padding) and no width/padding classes that fight it —
// only the inner column bound below, because four short cards spread across
// the shell's `max-w-6xl` is a worse read than a bounded 2×2 grid.

import { useNavigate } from 'react-router';
import { useSession } from '@/lib/auth.ts';
import { firstNameFromSession } from '@/lib/format.ts';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import { Card } from '@/components/ui/Card.tsx';
import { Button } from '@/components/ui/Button.tsx';
import { ChatIcon, CalendarIcon, DocIcon, UsersIcon } from '@/components/ui/icons.tsx';

const SECTIONS = [
  {
    icon: <ChatIcon size={22} />,
    title: 'Maeve drafts your first response',
    body: 'Open a support pathway and Maeve prepares a first message to the employee — pick a tone, edit it, and send.',
  },
  {
    icon: <CalendarIcon size={22} />,
    title: 'Check-ins and return-to-work plans',
    body: 'Maeve preps a few pointers before each touch point, and helps you build a return-to-work plan when the time comes.',
  },
  {
    icon: <DocIcon size={22} />,
    title: 'Ask Maeve about policy or what to say',
    body: "Not sure what your organisation's bereavement policy covers, or how to phrase something? Ask Maeve from any support pathway.",
  },
  {
    icon: <UsersIcon size={22} />,
    title: 'Your team, at a glance',
    body: 'Your dashboard lists every support pathway you own, with anything overdue flagged first.',
  },
];

export function ManagerWelcome() {
  // `manager-shell`, not a route id of this page's own — nesting moved the
  // `requireRoles` loader (and so the session in loader data) up to the shell.
  const session = useSession('manager-shell');
  const navigate = useNavigate();
  const firstName = firstNameFromSession(session as { name?: string });

  function handleGetStarted() {
    // One destination at every width — `/manager` picks its own chrome by
    // breakpoint (`ManagerShell`), so there's nothing here to decide.
    void navigate('/manager', { replace: true });
  }

  return (
    <section className="mx-auto max-w-4xl space-y-8 md:space-y-10">
      <div className="flex flex-col items-center text-center">
        <MaeveAvatar size={100} />
        <h1 className="mt-6 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          {firstName ? `Welcome, ${firstName}.` : 'Welcome.'}
        </h1>
        <p className="mt-2 max-w-md text-base text-muted-foreground">
          A quick look at how Maeve helps you support your team through bereavement.
        </p>
      </div>

      {/* `h-full` on the card so the two cards in a grid row share a height —
          the bodies differ by a line and ragged card bottoms read as broken. */}
      <div className="grid gap-3 sm:grid-cols-2 md:gap-4">
        {SECTIONS.map((s) => (
          <Card key={s.title} className="flex h-full items-start gap-4 p-5">
            <span
              aria-hidden
              className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary-soft text-primary"
            >
              {s.icon}
            </span>
            <span>
              <span className="block text-base font-semibold text-foreground">{s.title}</span>
              <span className="mt-1 block text-sm text-muted-foreground">{s.body}</span>
            </span>
          </Card>
        ))}
      </div>

      <div className="flex justify-center">
        <Button variant="primary" onClick={handleGetStarted}>
          Get started
        </Button>
      </div>
    </section>
  );
}
